import React, { useCallback, useContext, useRef, useState } from 'react';

import { DialogProps, FormControl, Stack } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { SubClassContainer } from './SubClassContainer';
import { CLASS_CREATE } from '../../config/apiPaths';
import { ClassContext } from '../../context/ClassContext';
import { PasswordGenerator } from '../../utils/passwordGenerator';
import { getUrls } from '../../utils/url';
import { ClassCreatePayload, ClassDataUpdate } from '../../views/ClassModal/types';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';
import ControlledInput from '../forms/ControlledInput';

export type CreateSubClassDialogProps = DialogProps & {};

export const SubClassesCreateDialog = (props: CreateSubClassDialogProps) => {
    const { onClose, open } = props;
    const { t } = useTranslation('create_edit_class');
    const { api } = getUrls();
    const [confirming, setConfirming] = useState(false);

    const { handleSubmit, control, getValues } = useForm();
    const formRef = useRef<HTMLFormElement | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const {
        classData: { class: classRecord },
        userUuid
    } = useContext(ClassContext);
    const { fetchSubClasses } = useContainer(SubClassContainer);

    const generatePassword = useCallback(() => new PasswordGenerator().generatePassword({ length: 10 }), []);

    const handleClose = useCallback(() => {
        onClose?.({}, 'backdropClick');
    }, [onClose]);

    const buildCreatePayload = useCallback((): ClassCreatePayload => {
        const { name, description } = getValues();

        const classDataNew: ClassDataUpdate = {
            class_name: name,
            class_description: description,
            content_type: 'default',
            timezone: classRecord?.data?.timezone ?? '',
            class_code: ''
        };

        return {
            class_data: classDataNew,
            parent_id: classRecord?.sid,
            user_uuid: userUuid,
            status: 'published',
            meeting_obj: {
                topic: name,
                password: generatePassword(),
                agenda: '',
                settings: {
                    waiting_room: false,
                    meeting_authentication: false
                }
            },
            class_options: {}
        };
    }, [classRecord?.data?.timezone, classRecord?.sid, generatePassword, getValues, userUuid]);

    const onSubmit = async () => {
        try {
            setConfirming(true);
            await axios.post(`${api}${CLASS_CREATE}`, buildCreatePayload());
            await fetchSubClasses();
        } catch (err: any) {
            switch (err?.response?.status) {
                case 429:
                    enqueueSnackbar(t('error.create-too-many'), { variant: 'error' });
                    break;
                default:
                    enqueueSnackbar(t('error.create'), { variant: 'error' });
            }
        }
        setConfirming(false);
        handleClose();
    };

    const submitForm = () => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    };

    const handleConfirmed = () => {
        submitForm();
    };

    return (
        <ConfirmationDialog
            id="edit-session"
            open={open}
            onClose={handleClose}
            title={t('create_edit_class:create-title')}
            confirmLabel={t('class:sub-class.create-button')}
            onConfirm={handleConfirmed}
            confirming={confirming}
            setConfirming={setConfirming}
        >
            <Stack>
                <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="name"
                            control={control}
                            label={t('details.class-name')}
                            placeholder={t('details.enter-class-name')}
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="description"
                            control={control}
                            placeholder={t('details.enter-description')}
                            label={t('details.description')}
                        />
                    </FormControl>
                </form>
            </Stack>
        </ConfirmationDialog>
    );
};
